import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Modal,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Formik } from "formik";
import * as yup from "yup";
import { GraphQLClient } from "graphql-request";
import Header from "../../components/Header";
import MarkdownEditor from "../../components/MarkdownEditor";
import { useSnackbar } from "../../components/snackbarProvider.jsx";
import { graphqlEndpoint } from "../../config";
import formOptions from "../../config/formOptions.json";

import { READ_CONTACT_BY_ID } from "../../graph/contacts/queries";
import { UPDATE_CONTACT } from "../../graph/contacts/mutations";
import { ARCHIVE_INTERACTION } from "../../graph/interactions/mutations";

const client = new GraphQLClient(graphqlEndpoint);

const contactStatusValues =
  (formOptions?.contactStatusOptions || []).map((opt) => opt.value) || [];

const preferredCommsMethodValues =
  (formOptions?.preferredCommsMethodOptions || []).map((opt) => opt.value) || [];

const engagementLevelValues =
  (formOptions?.engagementLevelOptions || []).map((opt) => opt.value) || [];

const validationSchema = yup.object().shape({
  firstName: yup.string().required("Required"),
  lastName: yup.string().notRequired(),
  email: yup.string().email("Invalid email").nullable().notRequired(),
  phone: yup.string().nullable().notRequired(),
  jobTitle: yup.string().nullable().notRequired(),
  companyName: yup.string().nullable().notRequired(),
  source: yup.string().nullable().notRequired(),
  referredByName: yup.string().nullable().notRequired(),
  status: yup
    .string()
    .oneOf(contactStatusValues, "Invalid status")
    .required("Required"),
  preferredCommsMethod: yup
    .string()
    .oneOf(preferredCommsMethodValues, "Invalid preferred contact method")
    .nullable()
    .notRequired(),
  engagementLevel: yup
    .string()
    .oneOf(engagementLevelValues, "Invalid engagement level")
    .nullable()
    .notRequired(),
  fitScore: yup
    .mixed()
    .test("fitScore", "Fit Score must be between 1 and 5", (value) => {
      if (value === "" || value === null || value === undefined) return true;
      const num = Number(value);
      return Number.isInteger(num) && num >= 1 && num <= 5;
    }),
  notes: yup.string().nullable().notRequired(),
  nextFollowUpAt: yup.string().nullable().notRequired(),
});

const formatDateTime = (value) => {
  if (!value) return "";
  return new Date(value).toLocaleString();
};

const EditContactForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [initialValues, setInitialValues] = useState(null);
  const [interactions, setInteractions] = useState([]);
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [selectedInteraction, setSelectedInteraction] = useState(null);
  const { setSnackbar } = useSnackbar();

  useEffect(() => {
    const fetchContact = async () => {
      try {
        const { readContactByID } = await client.request(READ_CONTACT_BY_ID, { id });

        setInitialValues({
          id: readContactByID.id,
          firstName: readContactByID.firstName || "",
          lastName: readContactByID.lastName || "",
          email: readContactByID.email || "",
          phone: readContactByID.phone || "",
          jobTitle: readContactByID.jobTitle || "",
          companyName: readContactByID.companyName || "",
          status: readContactByID.status || "TARGETED",
          source: readContactByID.source || "",
          referredByName: readContactByID.referredByName || "",
          preferredCommsMethod: readContactByID.preferredCommsMethod || "",
          engagementLevel: readContactByID.engagementLevel || "",
          fitScore:
            readContactByID.fitScore === null || readContactByID.fitScore === undefined
              ? ""
              : String(readContactByID.fitScore),
          notes: readContactByID.notes || "",
          nextFollowUpAt: readContactByID.nextFollowUpAt
            ? readContactByID.nextFollowUpAt.slice(0, 16)
            : "",
        });

        setInteractions(
          (readContactByID.interactions || []).map((interaction) => ({
            ...interaction,
            id: interaction.id,
            happenedAtFormatted: formatDateTime(interaction.happenedAt),
          }))
        );
      } catch (err) {
        console.error("Failed to load contact:", err);
        console.error("GraphQL response errors:", err.response?.errors);
        setSnackbar({
          open: true,
          severity: "error",
          message: "Failed to load contact",
        });
      }
    };

    fetchContact();
  }, [id, setSnackbar]);

  const handleSubmit = async (values) => {
    try {
      const input = {
        id: values.id,
        firstName: values.firstName,
        lastName: values.lastName || null,
        email: values.email || null,
        phone: values.phone || null,
        jobTitle: values.jobTitle || null,
        companyName: values.companyName || null,
        status: values.status,
        source: values.source || null,
        referredByName: values.referredByName || null,
        preferredCommsMethod: values.preferredCommsMethod || null,
        engagementLevel: values.engagementLevel || null,
        fitScore: values.fitScore === "" ? null : Number(values.fitScore),
        notes: values.notes || null,
        nextFollowUpAt: values.nextFollowUpAt
          ? new Date(values.nextFollowUpAt).toISOString()
          : null,
      };

      await client.request(UPDATE_CONTACT, { input });

      setSnackbar({
        open: true,
        severity: "success",
        message: "Contact updated successfully",
      });

      navigate("/manageContacts");
    } catch (err) {
      console.error("Update failed:", err);
      console.error("GraphQL response errors:", err.response?.errors);
      setSnackbar({
        open: true,
        severity: "error",
        message: err?.response?.errors?.[0]?.message || "Failed to update contact",
      });
    }
  };

  const handleOpenArchiveModal = (interaction) => {
    setSelectedInteraction(interaction);
    setArchiveModalOpen(true);
  };

  const handleCloseArchiveModal = () => {
    setArchiveModalOpen(false);
    setSelectedInteraction(null);
  };

  const handleConfirmArchive = async () => {
    if (!selectedInteraction?.id) return;

    try {
      await client.request(ARCHIVE_INTERACTION, { id: selectedInteraction.id });

      setInteractions((prev) =>
        prev.filter((interaction) => interaction.id !== selectedInteraction.id)
      );

      setArchiveModalOpen(false);
      setSelectedInteraction(null);

      setSnackbar({
        open: true,
        severity: "success",
        message: "Interaction archived successfully",
      });
    } catch (err) {
      console.error("Failed to archive interaction:", err);
      console.error("GraphQL response errors:", err.response?.errors);
      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to archive interaction",
      });
    }
  };

  if (!initialValues) return <div>Loading...</div>;

  return (
    <Box>
      <Header
        title="EDIT CONTACT"
        subtitle={`Edit ${[initialValues.firstName, initialValues.lastName]
          .filter(Boolean)
          .join(" ")}`}
      />

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ 
          values, 
          handleChange, 
          handleBlur, 
          handleSubmit, 
          setFieldValue,
          touched, 
          errors }) => (
          <form onSubmit={handleSubmit}>
            <Box display="grid" gap="20px" gridTemplateColumns="repeat(4, 1fr)">
              <TextField
                label="First Name"
                name="firstName"
                value={values.firstName}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.firstName && !!errors.firstName}
                helperText={touched.firstName && errors.firstName}
                sx={{ gridColumn: "span 2" }}
              />

              <TextField
                label="Last Name"
                name="lastName"
                value={values.lastName}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.lastName && !!errors.lastName}
                helperText={touched.lastName && errors.lastName}
                sx={{ gridColumn: "span 2" }}
              />

              <TextField
                label="Email"
                name="email"
                value={values.email}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.email && !!errors.email}
                helperText={touched.email && errors.email}
                sx={{ gridColumn: "span 2" }}
              />

              <TextField
                label="Phone Number"
                name="phone"
                value={values.phone}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.phone && !!errors.phone}
                helperText={touched.phone && errors.phone}
                sx={{ gridColumn: "span 2" }}
              />

              <TextField
                label="Job Title"
                name="jobTitle"
                value={values.jobTitle}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.jobTitle && !!errors.jobTitle}
                helperText={touched.jobTitle && errors.jobTitle}
                sx={{ gridColumn: "span 2" }}
              />

              <TextField
                label="Company Name"
                name="companyName"
                value={values.companyName}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.companyName && !!errors.companyName}
                helperText={touched.companyName && errors.companyName}
                sx={{ gridColumn: "span 2" }}
              />

              <FormControl fullWidth sx={{ gridColumn: "span 2" }}>
                <InputLabel>Source</InputLabel>
                <Select
                  name="source"
                  value={values.source}
                  label="Source"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!touched.source && !!errors.source}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {(formOptions.contactSourceOptions || []).map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Referred By Name"
                name="referredByName"
                value={values.referredByName}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.referredByName && !!errors.referredByName}
                helperText={touched.referredByName && errors.referredByName}
                sx={{ gridColumn: "span 2" }}
              />

              <FormControl fullWidth sx={{ gridColumn: "span 2" }}>
                <InputLabel>Status</InputLabel>
                <Select
                  name="status"
                  value={values.status}
                  label="Status"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!touched.status && !!errors.status}
                >
                  {(formOptions.contactStatusOptions || []).map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth sx={{ gridColumn: "span 2" }}>
                <InputLabel>Preferred Contact Method</InputLabel>
                <Select
                  name="preferredCommsMethod"
                  value={values.preferredCommsMethod}
                  label="Preferred Contact Method"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!touched.preferredCommsMethod && !!errors.preferredCommsMethod}
                >
                  {(formOptions.preferredCommsMethodOptions || []).map((opt) => (
                    <MenuItem key={opt.value || "none"} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth sx={{ gridColumn: "span 2" }}>
                <InputLabel>Engagement Level</InputLabel>
                <Select
                  name="engagementLevel"
                  value={values.engagementLevel}
                  label="Engagement Level"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!touched.engagementLevel && !!errors.engagementLevel}
                >
                  {(formOptions.engagementLevelOptions || []).map((opt) => (
                    <MenuItem key={opt.value || "none"} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth sx={{ gridColumn: "span 2" }}>
                <InputLabel>Fit Score</InputLabel>
                <Select
                  name="fitScore"
                  value={values.fitScore}
                  label="Fit Score"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!touched.fitScore && !!errors.fitScore}
                >
                  {(formOptions.fitScoreOptions || []).map((opt) => (
                    <MenuItem key={String(opt.value)} value={String(opt.value)}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Next Follow Up"
                name="nextFollowUpAt"
                type="datetime-local"
                value={values.nextFollowUpAt}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.nextFollowUpAt && !!errors.nextFollowUpAt}
                helperText={touched.nextFollowUpAt && errors.nextFollowUpAt}
                InputLabelProps={{ shrink: true }}
                sx={{ gridColumn: "span 2" }}
              />

              <MarkdownEditor
                value={values.notes}
                onChange={(nextValue) => setFieldValue("notes", nextValue)}
                label="Notes"
                minRows={14}
                error={!!touched.notes && !!errors.notes}
                helperText={touched.notes && errors.notes}
                sx={{ gridColumn: "span 4" }}
              />
            </Box>

            <Box display="flex" justifyContent="flex-end" mt={3}>
              <Button type="submit" variant="contained" color="secondary">
                Save Changes
              </Button>
            </Box>
          </form>
        )}
      </Formik>

      <Box mt="40px">
        <Header title="INTERACTIONS" subtitle="Recent interactions for this contact" />

        <Button
          variant="contained"
          color="secondary"
          size="small"
          onClick={() =>
            navigate("/createInteraction", {
              state: {
                contactID: id,
                redirectPath: `/contacts/edit/${id}`,
              },
            })
          }
        >
          + Interaction
        </Button>

        <Box mt={2}>
          <DataGrid
            autoHeight
            rows={interactions}
            columns={[
              { field: "type", headerName: "Type", flex: 1 },
              { field: "direction", headerName: "Direction", flex: 1 },
              { field: "summary", headerName: "Summary", flex: 2 },
              { field: "happenedAtFormatted", headerName: "Happened At", flex: 1.4 },
              {
                field: "actions",
                headerName: "Actions",
                flex: 1.4,
                sortable: false,
                renderCell: ({ row }) => (
                  <Box display="flex" gap="10px">
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() =>
                        navigate(`/interactions/edit/${row.id}`, {
                          state: { redirectPath: `/contacts/edit/${id}` },
                        })
                      }
                    >
                      View / Edit
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      color="warning"
                      onClick={() => handleOpenArchiveModal(row)}
                    >
                      Archive
                    </Button>
                  </Box>
                ),
              },
            ]}
          />
        </Box>
      </Box>

      <Modal open={archiveModalOpen} onClose={handleCloseArchiveModal}>
        <Box
          p={4}
          bgcolor="background.paper"
          borderRadius="12px"
          mx="auto"
          my="20vh"
          width="400px"
          boxShadow={24}
        >
          <Typography variant="h6">Archive Interaction</Typography>
          <Typography mb={2}>
            Are you sure you want to archive this interaction
            {selectedInteraction?.summary ? `: "${selectedInteraction.summary}"` : ""}?
          </Typography>
          <Box display="flex" justifyContent="flex-end" gap={2}>
            <Button onClick={handleCloseArchiveModal}>Cancel</Button>
            <Button color="warning" variant="contained" onClick={handleConfirmArchive}>
              Archive
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default EditContactForm;